import Vue from 'vue';
import { cloneDeep } from 'lodash';
import UserGridOptions from '@/services/User/UserGridOptions';
import * as types from '../mutation-types';

// state
export const state = {
  ...UserGridOptions.getGridOptionNamesState(),
  loaded: false,
  fetchFailed: false,
};

// mutations
export const mutations = {
  [types.SAVE_GRID_OPTIONS_SUCCESS](state, { name, config, sort, filter }) {
    state[name].grid = {
      state: config,
      sort,
      filter,
    };
  },
  [types.SAVE_GRID_OPTIONS_FAILURE](state, error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save grid preferences', error);

    Vue.notify({
      title: 'Preferences not saved',
      text: 'Your column layout could not be saved. Please try again.',
      type: 'error',
    });
  },
  [types.SAVING_GRID_OPTIONS](state) {},
  [types.FETCHING_GRID_OPTIONS](state) {
    state.loaded = false;
    state.fetchFailed = false;
  },
  [types.FETCH_GRID_OPTIONS_SUCCESS](state, config) {
    // Reset every known name first so any name absent from the server
    // response does NOT retain a stale config (e.g. from the previous
    // client). Then apply the response on top.
    Object.keys(state).forEach((name) => {
      if (name === 'loaded' || name === 'fetchFailed') return;
      if (state[name] && state[name].grid) {
        state[name].grid.state = null;
        state[name].grid.sort = null;
        state[name].grid.filter = null;
      }
    });

    Object.entries(config).forEach(([name, grid]) => {
      if (!state[name] || !state[name].grid) return;
      state[name].grid.state = grid.state;
      state[name].grid.sort = grid.sort;
      state[name].grid.filter = grid.filter;
    });

    state.loaded = true;
    state.fetchFailed = false;
  },
  [types.FETCH_GRID_OPTIONS_FAILURE](state) {
    // Mark as settled so consumers waiting for first load don't hang
    // forever on a transient API failure. Getters return null, callers
    // fall back to gridDefaults.
    state.loaded = true;
    state.fetchFailed = true;

    Vue.notify({
      title: 'Preferences unavailable',
      text: 'Your saved grid layouts could not be loaded, so changes will not be saved. Please reload the page.',
      type: 'error',
    });
  },
  [types.RESET_GRID_OPTIONS](state, name) {
    state[name].grid.state = null;
    state[name].grid.sort = null;
    state[name].grid.filter = null;
  }
};

// actions
export const actions = {

  /**
   * @param commit
   * @param status
   * @param config
   * @returns {Promise<void>}
   */
  async saveGridOptions({ commit, rootGetters, state: moduleState }, {
    name, state, sort, filter,
  }) {
    if (moduleState.fetchFailed) {
      return;
    }

    commit(types.SAVING_GRID_OPTIONS, name);
    const clientId = rootGetters.userClientId;
    const params = {  name, state, sort, filter, clientId };
    try {
      await UserGridOptions.saveState(params);
      params.config = state;
      commit(types.SAVE_GRID_OPTIONS_SUCCESS, params);
    } catch (e) {
      commit(types.SAVE_GRID_OPTIONS_FAILURE, e);
    }
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  async fetchGridOptions({ commit, rootGetters }) {
    commit(types.FETCHING_GRID_OPTIONS);

    try {
      const response = await UserGridOptions.getState(rootGetters.userClientId);
      commit(types.FETCH_GRID_OPTIONS_SUCCESS, response.data.data);
    } catch (e) {
      commit(types.FETCH_GRID_OPTIONS_FAILURE);
    }
  },

  resetLocalStorageGridOptions({ commit }, name) {
    commit(types.RESET_GRID_OPTIONS, name);
  }
};

// getters
export const getters = {
  gridSort: state => (name) => {
    if (!state[name] || !state[name].grid) {
      return null;
    }

    return cloneDeep(state[name].grid.sort);
  },
  gridFilter: state => (name) => {
    if (!state[name] || !state[name].grid) {
      return null;
    }

    return cloneDeep(state[name].grid.filter);
  },
  gridState: state => (name) => {
    if (!state[name] || !state[name].grid) {
      return null;
    }

    const saved = state[name].grid.state;

    if (Array.isArray(saved) && saved.length === 0) {
      return null;
    }

    return cloneDeep(saved);
  },
  gridOptionsLoaded: state => state.loaded,
};
